import { useEffect, useState, useRef } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ModelosAgrupados } from "@/components/ModelosAgrupados";
import { AnchoredList } from "@/components/AnchoredList";

const prefijoDe = (codigo) => String(codigo || "").trim().toUpperCase().split("-")[0];

const vacio = { codigo_parte: "", nombre: "", categoria: "", color: "", es_plastico: false, compatibilidad: "", notas: "", nombres_alternativos: "", grupo_equivalencia_id: null, modelo_ids: [], plantilla_heredada_id: null };

export const RepuestoFormDialog = ({ open, repuesto, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(vacio);
  const [grupos, setGrupos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [compatibles, setCompatibles] = useState([]);
  const [qComp, setQComp] = useState("");
  const [resComp, setResComp] = useState([]);
  const [componentes, setComponentes] = useState([]);
  const [qComponente, setQComponente] = useState("");
  const [resComponente, setResComponente] = useState([]);
  const [autoModelos, setAutoModelos] = useState(0);
  const compRef = useRef(null);
  const componenteRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setCompatibles([]); setQComp(""); setResComp([]); setAutoModelos(0);
    setComponentes([]); setQComponente(""); setResComponente([]);
    setForm(repuesto ? { ...vacio, ...repuesto, modelo_ids: repuesto.modelo_ids || [],
                         plantilla_heredada_id: repuesto.plantilla_heredada_id || null } : vacio);
    if (repuesto) {
      api.get(`/repuestos/${repuesto.id}`)
        .then(({ data }) => {
          setCompatibles(data.repuestos_compatibles_info || []);
          setComponentes(data.componentes_info || []);
        })
        .catch(() => {});
      // Autollenar Modelos compatibles desde catálogos de referencia (unión con los actuales).
      api.get(`/repuestos/${repuesto.id}/modelos-catalogo`)
        .then(({ data }) => {
          const detectados = data || [];
          setForm((f) => {
            const actuales = new Set(f.modelo_ids || []);
            const nuevos = detectados.filter((id) => !actuales.has(id));
            setAutoModelos(nuevos.length);
            return nuevos.length ? { ...f, modelo_ids: [...(f.modelo_ids || []), ...nuevos] } : f;
          });
        })
        .catch(() => {});
    }
    api.get("/grupos-equivalencia").then((r) => setGrupos(r.data)).catch(() => {});
    api.get("/modelos").then((r) => setModelos(r.data)).catch(() => {});
    api.get("/plantillas").then((r) => setPlantillas(r.data)).catch(() => {});
  }, [open, repuesto]);

  useEffect(() => {
    const term = qComp.trim();
    if (!term) { setResComp([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/repuestos", { params: { q: term, limit: 8 } });
        setResComp(data.filter((r) => r.id !== repuesto?.id && !compatibles.some((c) => c.id === r.id)));
      } catch { setResComp([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [qComp, compatibles, repuesto]);

  useEffect(() => {
    const term = qComponente.trim();
    if (!term) { setResComponente([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/repuestos", { params: { q: term, limit: 8 } });
        setResComponente(data.filter((r) => r.id !== repuesto?.id && !componentes.some((c) => c.prefijo === prefijoDe(r.codigo_parte))));
      } catch { setResComponente([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [qComponente, componentes, repuesto]);

  const agregarComponentePorPrefijo = (pref, nombre) => {
    const prefijo = prefijoDe(pref);
    if (!prefijo) return;
    if (componentes.some((c) => c.prefijo === prefijo)) {
      toast.error(`El prefijo "${prefijo}" ya está en el kit`);
      return;
    }
    setComponentes((c) => [...c, { prefijo, nombre: nombre || "", cantidad: 1 }]);
    setQComponente(""); setResComponente([]);
  };

  const toggleModelo = (id) =>
    setForm((f) => ({ ...f, modelo_ids: f.modelo_ids.includes(id) ? f.modelo_ids.filter((m) => m !== id) : [...f.modelo_ids, id] }));

  const guardar = async () => {
    const payload = { codigo_parte: form.codigo_parte, nombre: form.nombre, categoria: form.categoria,
      color: (form.color || "").trim(), es_plastico: !!form.es_plastico, notas: form.notas, nombres_alternativos: form.nombres_alternativos,
      grupo_equivalencia_id: form.grupo_equivalencia_id || null, modelo_ids: form.modelo_ids,
      repuestos_compatibles: compatibles.map((c) => c.id),
      componentes: componentes.map((c) => ({ prefijo: c.prefijo, nombre: c.nombre || "", cantidad: Number(c.cantidad) || 1 })),
      frt: form.frt === "" || form.frt === null || form.frt === undefined ? null : Number(form.frt),
      plantilla_heredada_id: form.plantilla_heredada_id || null };
    try {
      if (repuesto) await api.patch(`/repuestos/${repuesto.id}`, payload);
      else await api.post("/repuestos", payload);
      toast.success(repuesto ? "Repuesto actualizado" : "Repuesto creado");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(errorMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[90vh] flex flex-col">
        <DialogHeader><DialogTitle>{repuesto ? "Editar repuesto" : "Nuevo repuesto"}</DialogTitle></DialogHeader>
        <div className="space-y-3 flex-1 overflow-y-auto pr-1 -mr-1">
          <div className="space-y-1"><Label>Código de parte *</Label>
            <Input data-testid="repuesto-codigo-input" value={form.codigo_parte} onChange={(e) => setForm({ ...form, codigo_parte: e.target.value })} /></div>
          <div className="space-y-1"><Label>Nombre *</Label>
            <Input data-testid="repuesto-nombre-input" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
          <div className="space-y-1"><Label>Nombres alternativos / sinónimos</Label>
            <Input data-testid="repuesto-alt-input" value={form.nombres_alternativos} placeholder="Ej: oring, sello de varilla"
              onChange={(e) => setForm({ ...form, nombres_alternativos: e.target.value })} />
            <p className="text-xs text-muted-foreground">Otros nombres con los que se conoce este repuesto, separados por comas. Ayudan a encontrarlo en las búsquedas.</p></div>
          <div className="space-y-1"><Label>Categoría</Label>
            <Input data-testid="repuesto-categoria-input" value={form.categoria} placeholder="Ej: frenos, motor..." onChange={(e) => setForm({ ...form, categoria: e.target.value })} /></div>
          <div className="space-y-1"><Label>Color (piezas pintadas, opcional)</Label>
            <Input data-testid="repuesto-color-input" value={form.color || ""} placeholder="Ej: NH196, R264C"
              onChange={(e) => setForm({ ...form, color: e.target.value })} />
            <p className="text-xs text-muted-foreground">Código de color de fábrica para tapas, carenados y guardabarros pintados. Dejar vacío si no aplica.</p></div>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox data-testid="repuesto-plastico-check" checked={!!form.es_plastico}
              onCheckedChange={(v) => setForm({ ...form, es_plastico: !!v })} />
            <span className="text-sm">Panel plástico de carrocería (tapa, carenado, cubierta, guardabarro, visera)</span>
          </label>
          <div className="space-y-1"><Label>F.R.T. individual (horas, opcional)</Label>
            <Input data-testid="repuesto-frt-input" type="number" min="0" step="0.1" value={form.frt ?? ""}
              placeholder="Ej: 0.3" onChange={(e) => setForm({ ...form, frt: e.target.value })} />
            <p className="text-xs text-muted-foreground">Tiempo de cambio cuando el repuesto se instala solo (sin desarme compartido de una operación).</p></div>
          <div className="space-y-1"><Label>Repuestos compatibles</Label>
            <div className="relative" ref={compRef}>
              <Input data-testid="repuesto-compatibles-input" value={qComp} placeholder="Buscar por nombre o código de parte..."
                onChange={(e) => setQComp(e.target.value)} />
              <AnchoredList anchorRef={compRef} open={resComp.length > 0} className="rounded-md">
                <div data-testid="repuesto-compatibles-resultados">
                  {resComp.map((r) => (
                    <button key={r.id} type="button" data-testid={`repuesto-compatible-opcion-${r.id}`}
                      onClick={() => { setCompatibles((c) => [...c, { id: r.id, codigo_parte: r.codigo_parte, nombre: r.nombre }]); setQComp(""); setResComp([]); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0">
                      <span className="font-mono text-xs text-slate-500 shrink-0">{r.codigo_parte}</span>
                      <span className="truncate">{r.nombre}</span>
                    </button>
                  ))}
                </div>
              </AnchoredList>
            </div>
            {compatibles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {compatibles.map((c) => (
                  <span key={c.id} data-testid={`repuesto-compatible-chip-${c.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-0.5 text-xs">
                    <span className="font-mono">{c.codigo_parte}</span> {c.nombre}
                    <button type="button" title="Quitar" data-testid={`repuesto-compatible-quitar-${c.id}`}
                      onClick={() => setCompatibles((prev) => prev.filter((x) => x.id !== c.id))}
                      className="text-amber-500 hover:text-amber-800">×</button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Sustitutos intercambiables. La relación es mutua: el otro repuesto también quedará como compatible con este.</p></div>
          <div className="space-y-1"><Label>Componentes del kit (opcional)</Label>
            <div className="relative" ref={componenteRef}>
              <Input data-testid="repuesto-componentes-input" value={qComponente} placeholder="Buscar por nombre/código, o escribir un prefijo manual..."
                onChange={(e) => setQComponente(e.target.value)} />
              <AnchoredList anchorRef={componenteRef} open={resComponente.length > 0} className="rounded-md">
                <div data-testid="repuesto-componentes-resultados">
                  {resComponente.map((r) => (
                    <button key={r.id} type="button" data-testid={`repuesto-componente-opcion-${r.id}`}
                      onClick={() => agregarComponentePorPrefijo(r.codigo_parte, r.nombre)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-50 last:border-0">
                      <span className="font-mono text-xs text-slate-500 shrink-0">{r.codigo_parte}</span>
                      <span className="truncate">{r.nombre}</span>
                    </button>
                  ))}
                </div>
              </AnchoredList>
            </div>
            {qComponente.trim().length > 0 && resComponente.length === 0 && (
              <Button type="button" variant="outline" size="sm" data-testid="repuesto-componente-agregar-manual-btn"
                className="mt-1 h-7 text-xs"
                onClick={() => agregarComponentePorPrefijo(qComponente, "")}>
                Agregar prefijo manual "{prefijoDe(qComponente)}"
              </Button>
            )}
            {componentes.length > 0 && (
              <div className="space-y-1 pt-1">
                {componentes.map((c) => (
                  <div key={c.prefijo} data-testid={`repuesto-componente-fila-${c.prefijo}`}
                    className="flex items-center gap-2 rounded-md bg-violet-50 border border-violet-200 px-2.5 py-1 text-xs">
                    <span className="font-mono text-violet-600 shrink-0">[{c.prefijo}]</span>
                    <span className="text-violet-400 shrink-0">·</span>
                    <span className="flex-1 truncate text-violet-800">{c.nombre || "(sin nombre)"}</span>
                    <span className="text-violet-500 shrink-0">×</span>
                    <Input data-testid={`repuesto-componente-cantidad-${c.prefijo}`} type="number" min="0.01" step="1"
                      value={c.cantidad} onChange={(e) => setComponentes((prev) => prev.map((x) => x.prefijo === c.prefijo ? { ...x, cantidad: e.target.value } : x))}
                      className="h-6 w-16 text-xs px-1.5" />
                    <button type="button" title="Quitar" data-testid={`repuesto-componente-quitar-${c.prefijo}`}
                      onClick={() => setComponentes((prev) => prev.filter((x) => x.prefijo !== c.prefijo))}
                      className="text-violet-500 hover:text-violet-800">×</button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Si este repuesto es un KIT que ya incluye estas piezas, al agregarlo a un trabajo las piezas sueltas de otros servicios que compartan el mismo prefijo se marcarán "Ya incluido" automáticamente.</p></div>
          <div className="space-y-1">
            <Label>Modelos compatibles</Label>
            {autoModelos > 0 && (
              <p data-testid="repuesto-modelos-autollenado" className="text-xs text-emerald-700">
                Se premarcaron {autoModelos} modelo(s) detectados en catálogos de referencia. Podés ajustar antes de guardar.
              </p>
            )}
            <ModelosAgrupados modelos={modelos} seleccionados={form.modelo_ids} testPrefix="repuesto-modelo"
              onChange={(ids) => setForm({ ...form, modelo_ids: ids })}
              emptyMsg="No hay modelos registrados en el catálogo." />
          </div>
          <div className="space-y-1"><Label>Notas</Label>
            <Input data-testid="repuesto-notas-input" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
          <div className="space-y-1"><Label>Grupo de equivalencia</Label>
            <Select value={form.grupo_equivalencia_id || "ninguno"}
              onValueChange={(v) => setForm({ ...form, grupo_equivalencia_id: v === "ninguno" ? null : v })}>
              <SelectTrigger data-testid="repuesto-grupo-eq-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ninguno">Sin grupo</SelectItem>
                {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Los repuestos del mismo grupo son sustitutos intercambiables.</p></div>
          <div className="space-y-1"><Label>Plantilla heredada (opcional)</Label>
            <Select value={form.plantilla_heredada_id || "ninguna"}
              onValueChange={(v) => setForm({ ...form, plantilla_heredada_id: v === "ninguna" ? null : v })}>
              <SelectTrigger data-testid="repuesto-plantilla-heredada-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ninguna">Sin herencia</SelectItem>
                {plantillas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Al agregar este repuesto a una cotización se agregarán también todos los repuestos y la mano de obra de esta plantilla (recursivo, sin duplicar).</p></div>
        </div>
        <DialogFooter className="border-t border-slate-100 pt-3 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="guardar-repuesto-btn" onClick={guardar} disabled={!form.codigo_parte || !form.nombre}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
